import React, { useMemo, useState, useEffect } from 'react';
import { GraphLink, GraphNode, ComponentColors } from './types';
import { getDescendants, getTypeTier, getSymbol } from './utils/graphUtils';
import { ChevronRight, ChevronLeft, X, Layers, Zap, Maximize2, Minimize2, List, Info, GitBranch, Filter } from 'lucide-react';

interface DetailsPanelProps {
  selectedNodes: GraphNode[];
  onClose: () => void;
  nodes?: GraphNode[];
  links?: GraphLink[];
  onToggleType?: (nodeIds: string[], type: string) => void;
  expandedIds?: Set<string>;
  powerVisMode: 'none' | 'shadow' | 'halo' | 'bar';
  onPowerSelect?: (filter: { power: number; tier: number } | null) => void;
  onPropertySelect?: (key: string, value: any) => void;
  onLegendClick?: (type: string) => void;
  visibleNodeIds: Set<string>;
  onMultiNodeSelect?: (nodeIds: string[]) => void;
  isOpen: boolean;
  onToggle: () => void;
  // New Control Props
  orientation: 'TD' | 'LR';
  onOrientationChange: (o: 'TD' | 'LR') => void;
  onPowerVisChange: (m: 'none' | 'shadow' | 'halo' | 'bar') => void;
  onExpandAll: () => void;
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({
  selectedNodes = [],
  onClose,
  nodes = [],
  links = [],
  onToggleType,
  expandedIds,
  powerVisMode,
  onPowerSelect,
  onPropertySelect,
  onLegendClick,
  visibleNodeIds,
  onMultiNodeSelect,
  isOpen,
  onToggle,
  orientation,
  onOrientationChange,
  onPowerVisChange,
  onExpandAll
}) => {

  const [activeTab, setActiveTab] = useState<'legend' | 'details' | 'connections'>('legend');

  // Auto-switch tabs based on selection
  useEffect(() => {
    if (selectedNodes.length > 0) {
      setActiveTab('details');
    } else {
      setActiveTab('legend');
    }
  }, [selectedNodes]);

  // ... (Stats calculations remain the same) ...
  // 1. Calculate Electrical Hierarchy Stats (Downstream)
  const electricalStats = useMemo(() => {
    if (!selectedNodes.length || !links.length) return null;

    const counts: Record<string, number> = {};
    const firstIdOfType: Record<string, string> = {};

    selectedNodes.forEach(node => {
      const { nodeIds } = getDescendants(node.id, links);
      nodeIds.forEach(nid => {
        const n = nodes.find(gn => gn.id === nid);
        if (n) {
          counts[n.type] = (counts[n.type] || 0) + 1;
          if (!firstIdOfType[n.type]) firstIdOfType[n.type] = n.id;
        }
      });
    });

    return Object.entries(counts).sort((a, b) => {
      const tierA = getTypeTier(a[0]);
      const tierB = getTypeTier(b[0]);
      if (tierA !== tierB) return tierA - tierB;
      return b[1] - a[1];
    }).map(([type, count]) => ({ type, count, sampleId: firstIdOfType[type] }));
  }, [selectedNodes, links, nodes]);

  // 2. Calculate Path to Grid (upstream hierarchy with edges)
  interface PathStep {
    node: GraphNode;
    edge: GraphLink | null; // Edge connecting to the next node upstream (null for root)
    cumulativeLength: number; // Cumulative cable length from selected node
    cumulativePower: number; // Power at this node
  }

  const pathToGrid = useMemo(() => {
    if (!selectedNodes.length || selectedNodes.length > 1) return null;
    if (!nodes.length) return null;

    const selectedNode = selectedNodes[0];
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const path: PathStep[] = [];

    // Get the node from nodeMap to ensure we have parentId (selectedNodes may not have it)
    let currentNode: GraphNode | undefined = nodeMap.get(selectedNode.id) || selectedNode;
    let cumulativeLength = 0;

    // Walk up from selected node to root (grid)
    while (currentNode) {
      const nodeId = currentNode.id;
      const parentId = currentNode.parentId;

      // Find the edge connecting to parent (non-COMM edge where current is source)
      let connectingEdge: GraphLink | null = null;

      if (parentId) {
        connectingEdge = links.find(l =>
          l.source === nodeId &&
          l.target === parentId &&
          l.details.signal !== 'COMM'
        ) || null;

        // Get cable length from edge configuration or properties
        if (connectingEdge) {
          const config = connectingEdge.details.configuration as Record<string, any> | undefined;
          // Prefer Length (ft) from configuration, convert to meters for cumulative
          const lengthFt = Number(config?.['Length (ft)'] || config?.['length_ft'] || 0);
          const lengthM = Number(connectingEdge.details.properties?.['Cable Length (m)'] ||
                                 connectingEdge.details.properties?.['Length'] ||
                                 connectingEdge.details.properties?.['cable_length'] || 0);
          // Use ft if available (convert to m for cumulative), otherwise use m directly
          const edgeLength = lengthFt > 0 ? lengthFt * 0.3048 : lengthM;
          cumulativeLength += edgeLength;
        }
      }

      path.push({
        node: currentNode,
        edge: connectingEdge,
        cumulativeLength,
        cumulativePower: currentNode.cumulativePower || 0
      });

      // Move to parent
      if (parentId) {
        currentNode = nodeMap.get(parentId);
      } else {
        currentNode = undefined;
      }
    }

    return path;
  }, [selectedNodes, nodes, links]);

  // 3. Calculate Power Distribution (Individual component powers for selected type(s))
  const powerDistribution = useMemo(() => {
    if (!selectedNodes.length || !nodes.length) return null;

    // Get unique types from selected nodes
    const selectedTypes = new Set(selectedNodes.map(n => n.type));

    // Find all nodes of the same type(s) as selected nodes
    const sameTypeNodes = nodes.filter(n => selectedTypes.has(n.type));

    // Group by cumulative power
    const powerGroups: Record<number, GraphNode[]> = {};
    sameTypeNodes.forEach(n => {
      if (n.cumulativePower !== undefined && n.cumulativePower > 0) {
        const powerKey = Math.round(n.cumulativePower); // Round to avoid floating point issues
        if (!powerGroups[powerKey]) {
          powerGroups[powerKey] = [];
        }
        powerGroups[powerKey].push(n);
      }
    });

    // Total count of filtered components (for percentage calculation)
    const totalFilteredCount = sameTypeNodes.filter(n =>
      n.cumulativePower !== undefined && n.cumulativePower > 0
    ).length;

    // Convert to array and sort by power descending
    return Object.entries(powerGroups)
      .map(([powerStr, groupNodes]) => {
        const power = parseInt(powerStr);
        const count = groupNodes.length;
        // Percentage is count / total filtered count (e.g., 2 of 5 inverters = 40%)
        const percentage = totalFilteredCount > 0 ? (count / totalFilteredCount) * 100 : 0;
        return {
          power,
          count,
          percentage,
          nodeIds: groupNodes.map(n => n.id) // Store all node IDs for selection
        };
      })
      .sort((a, b) => b.power - a.power);
  }, [selectedNodes, nodes]);


  // Check visibility state for toggles
  const checkVisible = (type: string) => {
    if (!selectedNodes.length || !nodes.length || !visibleNodeIds) return false;
    const relevantDescendants: GraphNode[] = [];
    selectedNodes.forEach(node => {
      const { nodeIds } = getDescendants(node.id, links);
      nodeIds.forEach(nid => {
        const n = nodes.find(gn => gn.id === nid);
        if (n && n.type === type) {
          relevantDescendants.push(n);
        }
      });
    });
    if (relevantDescendants.length === 0) return false;
    return relevantDescendants.every(n => visibleNodeIds.has(n.id));
  };

  // Helper to render property chips
  const renderProperties = (objKey: 'properties' | 'configuration') => {
    if (!selectedNodes.length) return null;

    // 1. Collect all keys present in any selected node
    const allKeys = new Set<string>();
    selectedNodes.forEach(n => {
      if (n.details[objKey]) {
        Object.keys(n.details[objKey]!).forEach(k => allKeys.add(k));
      }
    });

    if (allKeys.size === 0) return null;

    return (
      <div>
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {objKey === 'properties' ? 'Properties' : 'Configuration'}
        </label>
        <div className="mt-2 bg-white/[0.08] rounded-md p-3 space-y-2 border border-white/10">
          {Array.from(allKeys).sort().map(key => {
            // Collect all values for this key
            const values = new Set<string>();
            selectedNodes.forEach(n => {
              if (n.details[objKey] && n.details[objKey]![key] !== undefined) {
                values.add(String(n.details[objKey]![key]));
              }
            });

            return (
              <div key={key} className="flex flex-col border-b border-white/10 last:border-0 pb-2 last:pb-0">
                <span className="text-xs text-gray-500 mb-1">{key}</span>
                <div className="flex flex-wrap gap-1">
                  {Array.from(values).sort().map(val => (
                    <button
                      key={val}
                      onClick={() => onPropertySelect && onPropertySelect(key, isNaN(Number(val)) ? val : Number(val))}
                      className="px-2 py-0.5 rounded text-xs font-mono bg-white/15 text-gray-200 hover:bg-emerald-600 hover:text-white transition-colors border border-white/20 hover:border-emerald-500"
                      title={`Select all with ${key} = ${val}`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 3. Get unique node types from the graph, separated into power and auxiliary
  const { powerComponents, auxiliaryComponents } = useMemo(() => {
    const typeData = new Map<string, { count: number; tier: number; isAncillary: boolean }>();

    nodes.forEach(n => {
      const existing = typeData.get(n.type);
      if (existing) {
        existing.count++;
      } else {
        typeData.set(n.type, {
          count: 1,
          tier: n.tier ?? 999,
          isAncillary: n.isAncillary ?? false
        });
      }
    });

    const allTypes = Array.from(typeData.entries()).map(([type, data]) => ({
      type,
      count: data.count,
      tier: data.tier,
      isAncillary: data.isAncillary,
      color: ComponentColors[type as keyof typeof ComponentColors] || '#9CA3AF'
    }));

    // Sort by tier (power flow position)
    const sortByTier = (a: typeof allTypes[0], b: typeof allTypes[0]) => a.tier - b.tier;

    return {
      powerComponents: allTypes.filter(t => !t.isAncillary).sort(sortByTier),
      auxiliaryComponents: allTypes.filter(t => t.isAncillary).sort(sortByTier)
    };
  }, [nodes]);


  // 4. Get unique edge types
  const edgeTypes = useMemo(() => {
    const types = new Set<string>();
    links.forEach(l => {
      if (l.details?.signal) types.add(l.details.signal);
    });
    return Array.from(types);
  }, [links]);

  // 5. Calculate counts of selected nodes by type (Always calculated to avoid hook violation)
  const selectedTypeCounts = useMemo(() => {
    if (selectedNodes.length <= 1) return null;
    const counts: Record<string, number> = {};
    selectedNodes.forEach(n => {
      counts[n.type] = (counts[n.type] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1]) // Sort by count desc
      .map(([type, count]) => ({ type, count }));
  }, [selectedNodes]);


  // --- Render Content Functions ---

  const renderLegend = () => {
    const formatTypeName = (type: string) => {
      return type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    const getEdgeColor = (signal: string) => {
      if (signal === 'AC') return '#ef4444';
      if (signal === 'DC') return '#10b981';
      if (signal === 'COMM') return '#3b82f6';
      return '#9CA3AF';
    };

    // Get unique representatives for a type when count > 200
    const getUniqueRepresentatives = (type: string): string[] => {
      const nodesOfType = nodes.filter(n => n.type === type);
      const uniqueByMetadata = new Map<string, GraphNode>();

      nodesOfType.forEach(n => {
        // Create a key from properties and configuration
        const propsKey = JSON.stringify(n.details?.properties || {});
        const configKey = JSON.stringify(n.details?.configuration || {});
        const metaKey = `${propsKey}|${configKey}`;

        if (!uniqueByMetadata.has(metaKey)) {
          uniqueByMetadata.set(metaKey, n);
        }
      });

      return Array.from(uniqueByMetadata.values()).map(n => n.id);
    };

    // Filter to select nodes of target type
    const handleFilterClick = (targetType: string) => {
      if (!onMultiNodeSelect) return;

      const nodesOfType = nodes.filter(n => n.type === targetType);
      const count = nodesOfType.length;

      let selectedIds: string[];
      if (count <= 200) {
        // Select all nodes of this type
        selectedIds = nodesOfType.map(n => n.id);
      } else {
        // Select unique representatives
        selectedIds = getUniqueRepresentatives(targetType);
      }

      onMultiNodeSelect(selectedIds);
    };

    const renderComponentList = (components: typeof powerComponents) => (
      <div className="space-y-1.5">
        {components.map(({ type, count, tier, color, isAncillary }) => {
          const isLargeSet = count > 200;
          const uniqueCount = isLargeSet ? getUniqueRepresentatives(type).length : count;

          return (
            <div
              key={type}
              className="flex items-center justify-between px-2 py-1.5 rounded transition-all group"
            >
              <div
                className={`flex items-center flex-1 ${onLegendClick && selectedNodes.length === 0 ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => onLegendClick && onLegendClick(type)}
                title={onLegendClick && selectedNodes.length === 0 ? `Click to filter to ${formatTypeName(type)}` : ''}
              >
                <svg width="20" height="20" viewBox="-20 -20 40 40" className="mr-2 flex-shrink-0">
                  <path
                    d={getSymbol(type)}
                    fill={color}
                    stroke={color}
                    strokeWidth="2"
                    opacity="0.8"
                  />
                </svg>
                <span className="text-xs text-gray-300">{formatTypeName(type)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{count}</span>
                <button
                  onClick={() => handleFilterClick(type)}
                  className="p-1 rounded transition-colors text-gray-500 hover:text-emerald-400 hover:bg-white/10 opacity-50 group-hover:opacity-100"
                  title={isLargeSet
                    ? `Select ${uniqueCount} unique representatives (${count} total)`
                    : `Select all ${count} ${formatTypeName(type)}`}
                >
                  <Filter size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="space-y-6">
        <p className="text-gray-400 italic text-sm">
          {selectedNodes.length === 0 ? "Select a component to view details." : "Graph Legend"}
        </p>

        {/* Power Components */}
        {powerComponents.length > 0 && (
          <div className="p-4 bg-white/[0.08] rounded-lg border border-white/10">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">Power Flow</h3>
            {renderComponentList(powerComponents)}

            {edgeTypes.filter(s => s !== 'COMM').length > 0 && (
              <>
                <div className="border-t border-white/10 my-3"></div>
                <div className="space-y-1.5">
                  {edgeTypes.filter(s => s !== 'COMM').map(signal => (
                    <div key={signal} className="flex items-center px-2 py-1">
                      <span
                        className="w-8 h-0.5 mr-2"
                        style={{ backgroundColor: getEdgeColor(signal) }}
                      ></span>
                      <span className="text-xs text-gray-300">{signal} Line</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Auxiliary Components */}
        {auxiliaryComponents.length > 0 && (
          <div className="p-4 bg-white/[0.08] rounded-lg border border-white/10">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">Auxiliary Systems</h3>
            {renderComponentList(auxiliaryComponents)}

            {edgeTypes.includes('COMM') && (
              <>
                <div className="border-t border-white/10 my-3"></div>
                <div className="space-y-1.5">
                  <div className="flex items-center px-2 py-1">
                    <span
                      className="w-8 h-0.5 mr-2"
                      style={{
                        backgroundImage: `repeating-linear-gradient(90deg, ${getEdgeColor('COMM')}, ${getEdgeColor('COMM')} 4px, transparent 4px, transparent 6px)`
                      }}
                    ></span>
                    <span className="text-xs text-gray-300">COMM Line</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDetails = () => {
    if (selectedNodes.length === 0) return <div className="text-gray-500 italic p-4">No component selected.</div>;

    const primaryNode = selectedNodes[0];
    const isMultiSelect = selectedNodes.length > 1;

    return (
      <div className="space-y-6">
        {!isMultiSelect && (
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</label>
            <div className="text-emerald-400 font-mono mt-1 break-words">{primaryNode.type}</div>
          </div>
        )}

        {!isMultiSelect && primaryNode.cumulativePower !== undefined && primaryNode.cumulativePower > 0 && (
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Cumulative Power</label>
            <div className="text-emerald-400 font-mono mt-1 break-words">
              {(primaryNode.cumulativePower / 1000).toFixed(2)} kW
            </div>
          </div>
        )}

        {/* Power Distribution Table */}
        {powerDistribution && powerDistribution.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Power Distribution - {Array.from(new Set(selectedNodes.map(n => n.type))).map(t => t.replace(/_/g, ' ')).join(', ')}
            </label>
            <div className="mt-2 bg-white/[0.08] rounded-lg border border-white/10 overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-white/10 text-gray-200">
                  <tr>
                    <th className="px-3 py-2">Power (kW)</th>
                    <th className="px-3 py-2 text-right">Count</th>
                    <th className="px-3 py-2 text-right">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {powerDistribution.map(({ power, count, percentage, nodeIds }) => (
                    <tr
                      key={power}
                      className="hover:bg-white/10 cursor-pointer transition-colors"
                      onClick={() => {
                        if (onMultiNodeSelect) {
                          onMultiNodeSelect(nodeIds);
                        }
                      }}
                      title={`Select all ${count} components with ${(power / 1000).toFixed(2)} kW`}
                    >
                      <td className="px-3 py-2 text-gray-300 font-mono">
                        {(power / 1000).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-400">{count}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-400">
                        {percentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {primaryNode.details.Aliases && primaryNode.details.Aliases.length > 0 && !isMultiSelect && (
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Aliases</label>
            <ul className="mt-1 list-disc list-inside text-sm text-gray-300 font-mono">
              {primaryNode.details.Aliases.map((alias, idx) => (
                <li key={idx} className="truncate">{alias}</li>
              ))}
            </ul>
          </div>
        )}

        {renderProperties('properties')}
        {renderProperties('configuration')}

        {/* Electrical Hierarchy (Downstream) */}
        {electricalStats && electricalStats.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Downstream Hierarchy</label>
            <div className="mt-2 bg-white/[0.08] rounded-lg border border-white/10 overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-white/10 text-gray-200">
                  <tr>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-right">{isMultiSelect ? 'Count/Per' : 'Count'}</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {electricalStats.map(({ type, count }) => {
                    const isVisible = checkVisible(type);
                    return (
                      <tr key={type} className="hover:bg-white/10 group transition-colors">
                        <td className="px-3 py-2 flex items-center text-gray-300">
                          <span
                            className="w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: ComponentColors[type] || '#9CA3AF' }}
                          ></span>
                          {type.replace(/_/g, ' ')}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-400">
                          {isMultiSelect ? `${count}/${Math.round(count / selectedNodes.length)}` : count}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {onToggleType && (
                            <button
                              onClick={() => onToggleType(selectedNodes.map(n => n.id), type)}
                              className={`p-1 rounded transition-colors ${isVisible ? 'text-emerald-400 hover:text-emerald-300' : 'text-gray-600 hover:text-gray-400'}`}
                              title={isVisible ? `Hide ${type}s` : `Show ${type}s`}
                            >
                              {isVisible ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderConnections = () => {
    if (selectedNodes.length === 0) return <div className="text-gray-500 italic p-4">No component selected.</div>;

    const isMultiSelect = selectedNodes.length > 1;

    // For multi-select, show a simple message
    if (isMultiSelect) {
      return (
        <div className="space-y-4">
          <p className="text-gray-400 italic text-sm">
            Select a single component to view the path to grid.
          </p>
        </div>
      );
    }

    // Get edge color
    const getEdgeColor = (signal: string) => {
      if (signal === 'AC') return '#ef4444';
      if (signal === 'DC') return '#10b981';
      if (signal === 'COMM') return '#3b82f6';
      return '#9CA3AF';
    };

    // Format type name
    const formatTypeName = (type: string) => {
      return type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    if (!pathToGrid || pathToGrid.length === 0) {
      return <div className="text-gray-500 italic text-sm p-4">No path to grid found.</div>;
    }

    // Reverse path so grid is at top
    const pathFromGrid = [...pathToGrid].reverse();

    return (
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Path to Grid</label>

        <div className="bg-white/[0.08] rounded-lg border border-white/10 p-3">
          {pathFromGrid.map((step, index) => {
            const isSelected = step.node.id === selectedNodes[0].id;
            const isRoot = index === 0;
            const nextStep = index < pathFromGrid.length - 1 ? pathFromGrid[index + 1] : null;

            // Edge details (the edge going DOWN to the next node)
            const edgeToNext = nextStep?.edge;
            const edgeSignal = edgeToNext?.details.signal || '';

            // Get wire configuration
            const config = edgeToNext?.details.configuration as Record<string, any> | undefined;
            const wireClass = config?.wire_class || config?.['Wire Class'] || '';
            const isMainWire = wireClass === 'main';
            const isStubWire = wireClass === 'stub';

            // Get length - prefer configuration, fallback to properties
            const edgeLengthFt = Number(config?.['Length (ft)'] || config?.['length_ft'] || 0);
            const edgeLengthM = Number(edgeToNext?.details.properties?.['Cable Length (m)'] ||
                                       edgeToNext?.details.properties?.['Length'] ||
                                       edgeToNext?.details.properties?.['cable_length'] || 0);
            const edgeLength = edgeLengthFt > 0 ? edgeLengthFt : edgeLengthM;
            const lengthUnit = edgeLengthFt > 0 ? 'ft' : 'm';

            // Get power/voltage drop from configuration
            const voltageDrop = Number(config?.['Voltage Drop (%)'] || config?.['voltage_drop_pct'] || 0);
            const powerLoss = Number(edgeToNext?.details.properties?.['Power Loss (W)'] ||
                                     edgeToNext?.details.properties?.['Loss'] ||
                                     edgeToNext?.details.properties?.['power_loss'] || 0);

            return (
              <div key={step.node.id}>
                {/* Node */}
                <div
                  className={`flex items-center gap-2 px-2 py-2 rounded transition-colors ${isSelected ? 'bg-emerald-500/20 border border-emerald-500/40' : 'hover:bg-white/5'}`}
                  onClick={() => onMultiNodeSelect && onMultiNodeSelect([step.node.id])}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Symbol */}
                  <svg width="24" height="24" viewBox="-20 -20 40 40" className="flex-shrink-0">
                    <path
                      d={getSymbol(step.node.type)}
                      fill={step.node.color}
                      stroke={step.node.color}
                      strokeWidth="2"
                      opacity="0.9"
                    />
                  </svg>

                  {/* Node Info */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium truncate ${isSelected ? 'text-emerald-400' : 'text-gray-200'}`}>
                      {step.node.id}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {formatTypeName(step.node.type)}
                    </div>
                  </div>

                  {/* Cumulative Stats */}
                  <div className="text-right flex-shrink-0">
                    {step.cumulativePower > 0 && (
                      <div className="text-xs font-mono text-emerald-400">
                        {(step.cumulativePower / 1000).toFixed(1)} kW
                      </div>
                    )}
                    {!isRoot && step.cumulativeLength > 0 && (
                      <div className="text-[10px] font-mono text-gray-500">
                        Σ {step.cumulativeLength.toFixed(1)}m
                      </div>
                    )}
                  </div>
                </div>

                {/* Edge connector (if not last) */}
                {nextStep && (
                  <div className="flex items-stretch ml-[11px] my-0.5">
                    {/* Vertical line - dashed for stub, solid for main */}
                    <div
                      className="w-0.5 min-h-[28px]"
                      style={{
                        backgroundColor: isStubWire ? 'transparent' : getEdgeColor(edgeSignal),
                        backgroundImage: isStubWire
                          ? `repeating-linear-gradient(180deg, ${getEdgeColor(edgeSignal)}, ${getEdgeColor(edgeSignal)} 3px, transparent 3px, transparent 6px)`
                          : 'none'
                      }}
                    />

                    {/* Edge details */}
                    <div className="ml-3 flex flex-col gap-0.5 text-[10px] text-gray-500">
                      <span className="font-medium" style={{ color: getEdgeColor(edgeSignal) }}>
                        {edgeSignal || '—'}
                      </span>
                      <div className="flex items-center gap-3">
                        {edgeLength > 0 && (
                          <span className="text-gray-400">
                            {edgeLength.toFixed(1)} {lengthUnit}
                          </span>
                        )}
                        {voltageDrop > 0 && (
                          <span className="text-amber-400">↓{voltageDrop.toFixed(2)}%</span>
                        )}
                        {powerLoss > 0 && (
                          <span className="text-red-400">-{(powerLoss / 1000).toFixed(2)}kW</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        {pathToGrid.length > 1 && (
          <div className="mt-3 p-2 bg-white/[0.04] rounded border border-white/5 text-xs text-gray-400">
            <div className="flex justify-between">
              <span>Total Path Length:</span>
              <span className="font-mono text-gray-300">
                {pathToGrid[pathToGrid.length - 1].cumulativeLength.toFixed(1)}m
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span>Hops to Grid:</span>
              <span className="font-mono text-gray-300">{pathToGrid.length - 1}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const primaryNode = selectedNodes[0];
  const isMultiSelect = selectedNodes.length > 1;

  return (
    <div
      className={`relative h-full border-l transition-all duration-300 ease-in-out flex flex-col z-20 ${isOpen ? 'w-96 translate-x-0' : 'w-0 translate-x-full border-none'}`}
      style={{
        background: 'rgba(13, 24, 48, 0.65)',
        backdropFilter: 'blur(16px) saturate(120%)',
        WebkitBackdropFilter: 'blur(16px) saturate(120%)',
        border: '0.5px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '-4px 0 32px 0 rgba(0, 0, 0, 0.4)'
      }}
    >
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -left-8 top-1/2 -translate-y-1/2 border border-white/20 border-r-0 rounded-l-md p-1 text-gray-400 hover:text-white shadow-lg z-30 w-8 h-12 flex items-center justify-center transition-colors backdrop-blur-lg bg-[#0d1830]/50"
        title={isOpen ? "Collapse Sidebar" : "Expand Sidebar"}
      >
        {isOpen ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
      </button>

      {/* Content Container */}
      <div className="flex-1 overflow-hidden w-96 flex flex-col bg-transparent">

        {/* Header Section */}
        <div className="shrink-0 border-b border-white/10 bg-transparent">

          {/* Top Bar: Title & Close */}
          <div className="p-4 flex justify-between items-center">
            <div className="truncate pr-2 flex-1">
              <h2 className="text-lg font-bold text-white" title={primaryNode?.id || "Graph Explorer"}>
                {selectedNodes.length === 0 ? "Graph Explorer" : (isMultiSelect ? `${selectedNodes.length} Items Selected` : primaryNode.id)}
              </h2>
              {isMultiSelect && selectedTypeCounts && (
                <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                  {selectedTypeCounts.map(({ type, count }) => (
                    <div key={type} className="flex items-center gap-1">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: ComponentColors[type as keyof typeof ComponentColors] || '#9CA3AF' }}
                      ></span>
                      <span>{count}× {type.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedNodes.length > 0 && (
              <button onClick={onClose} className="text-gray-400 hover:text-white focus:outline-none shrink-0 p-1 hover:bg-white/10 rounded transition-colors">
                <X size={18} />
              </button>
            )}
          </div>

          {/* Controls Bar */}
          <div className="px-4 pb-4 flex gap-2">
            <button
              onClick={() => onOrientationChange(orientation === 'TD' ? 'LR' : 'TD')}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-white/[0.08] hover:bg-white/15 text-gray-300 hover:text-white rounded text-xs font-medium transition-colors border border-white/10"
              title="Toggle Orientation"
            >
              <Layers size={14} />
              {orientation === 'TD' ? 'Vertical' : 'Horizontal'}
            </button>
            <button
              onClick={() => onPowerVisChange(powerVisMode === 'none' ? 'shadow' : powerVisMode === 'shadow' ? 'halo' : powerVisMode === 'halo' ? 'bar' : 'none')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors border ${powerVisMode !== 'none' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : 'bg-white/[0.08] hover:bg-white/15 text-gray-300 hover:text-white border-white/10'}`}
              title="Toggle Power Visualization"
            >
              <Zap size={14} />
              Power
            </button>
            <button
              onClick={onExpandAll}
              className="flex items-center justify-center px-3 py-1.5 bg-white/[0.08] hover:bg-white/15 text-gray-300 hover:text-white rounded text-xs font-medium transition-colors border border-white/10"
              title={expandedIds?.size && expandedIds.size > 0 ? "Collapse All" : "Expand All"}
            >
              {expandedIds?.size && expandedIds.size > 0 ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>

          {/* Tabs Bar */}
          <div className="flex border-t border-white/10">
            <button
              onClick={() => setActiveTab('legend')}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'legend' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'}`}
            >
              <List size={14} />
              Legend
            </button>
            <button
              onClick={() => setActiveTab('details')}
              disabled={selectedNodes.length === 0}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'details' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'} ${selectedNodes.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Info size={14} />
              Details
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              disabled={selectedNodes.length === 0}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'connections' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'} ${selectedNodes.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <GitBranch size={14} />
              Path
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scroll bg-transparent">
          {activeTab === 'legend' && renderLegend()}
          {activeTab === 'details' && renderDetails()}
          {activeTab === 'connections' && renderConnections()}
        </div>
      </div>
    </div>
  );
};

export default DetailsPanel;
